import { Component, ElementRef, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { getStoredProjectLogo } from '@app/utils/project-logo.util';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ContractFieldType } from '@app/enums/contract-field-type.enum';
import { ContractStatusEnum } from '@app/enums/contract-status.enum';
import { Project } from '@app/models/firebase/project';
import { Contract, ContractField, ContractSigner } from '@app/models/firebase/contract';
import { AuthService } from '@app/services/auth/auth.service';
import { ContractService } from '@app/services/contract/contract.service';
import { ToasterService } from '@app/services/toaster/toaster.service';
import { ContractPdfRendererService } from '@app/services/contract-pdf/contract-pdf-renderer.service';
import { StorageService } from '@app/services/storage/storage.service';
import { SpinnerService } from '@app/services/spinner/spinner.service';

@Component({
    selector: 'app-sign-contract',
    templateUrl: './sign-contract.component.html',
    styleUrls: ['./sign-contract.component.scss'],
    standalone: false
})
export class SignContractComponent implements OnInit {

  private static readonly MAX_FIELD_LABEL_LENGTH = 27;

  ContractFieldType = ContractFieldType;

  truncatedFieldLabel(field: ContractField): string {
    const label = this.fieldLabel(field) || '';
    const maxLength = SignContractComponent.MAX_FIELD_LABEL_LENGTH;
    return label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label;
  }

  fieldLabelTooltip(field: ContractField): string | null {
    const label = this.fieldLabel(field) || '';
    return label.length > SignContractComponent.MAX_FIELD_LABEL_LENGTH ? label : null;
  }

  projectLogo = getStoredProjectLogo();

  model = new Contract();
  contractId: string;
  projectId: string;
  signerToken: string | null = null;
  currentSigner: ContractSigner | null = null;
  isLoading = true;
  loadError: string = null;
  @ViewChild('signForm') signForm: TemplateRef<any>;
  dialogReference: any;
  loading = false;
  activeField: ContractField = null;
  zoom: number = 1.0;
  originalSize: boolean = false;
  autoresize: boolean = true;
  fitToPage: boolean = true;
  
  viewMode: 'FORM' | 'PDF' = 'FORM';
  completed = false;
  currentStepIndex: number = 0;

  
  _independentFields: ContractField[] = [];
  _tablesWithFields: any[] = [];
  _uniqueFields: ContractField[] = [];
  _pagesWithFormGroups: any[] = [];
  @ViewChild('signatureCanvas') canvas: ElementRef<HTMLCanvasElement>;
  private ctx: CanvasRenderingContext2D;
  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;
  /** Last evaluated visibility, used to clear values only on visible -> hidden transitions. */
  private fieldVisibility = new Map<string, boolean>();
  
  constructor(
    private authService: AuthService,
    private contractService: ContractService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private toasterService: ToasterService,
    private storageService: StorageService,
    private pdfRenderer: ContractPdfRendererService,
    private spinnerService: SpinnerService,
  ) { }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    this.loadError = null;

    try {
      const params = this.route.snapshot.params;
      this.contractId = params['id'] || params['contractId'];
      this.projectId = params['projectId'] || this.authService.projectId;
      this.signerToken = params['signerToken'] || params['token'] || null;

      if (this.signerToken) {
        [this.model, this.currentSigner] = await Promise.all([
          this.contractService.getContract(this.projectId, this.contractId),
          this.contractService.getSignerByToken(this.projectId, this.contractId, this.signerToken),
        ]);

        if (!this.model || !this.currentSigner) {
          this.loadError = 'Invalid or expired signature link.';
          this.toasterService.showError(this.loadError);
          return;
        }
      } else {
        this.model = await this.contractService.getContract(this.projectId, this.contractId);
        if (this.authService.auth?.user?.id) {
          this.currentSigner = await this.contractService.getSignerById(
            this.projectId,
            this.contractId,
            this.authService.auth.user.id
          );
        }
      }

      if (!this.model) {
        this.loadError = 'We could not load this contract.';
        this.toasterService.showError(this.loadError);
        return;
      }

      this.model.fields?.forEach(f => {
        if (f.value === undefined || f.value === null) {
          f.value = '';
        }
      });

      if (this.currentSigner) {
        if (this.currentSigner.fieldValues) {
          this.model.fields?.forEach(f => {
            if (this.currentSigner.fieldValues[f.id] !== undefined) {
              f.value = this.currentSigner.fieldValues[f.id];
            }
          });
        }

        if (this.currentSigner.status === ContractStatusEnum.SIGNED) {
          this.completed = true;
          this.viewMode = 'PDF';
          this.toasterService.show('You have already signed this document.', 'Information');
        }
      }

      this.captureFieldVisibility();
      this.refreshFormGroups();
    } catch (error) {
      this.loadError = 'We could not load this contract.';
      this.toasterService.showError(this.loadError);
    } finally {
      this.isLoading = false;
    }
  }

  signDocument() {
    if (!this.canSign()) {
      return;
    }

    this.dialogReference = this.dialog.open(this.signForm, {
      minWidth: '60vw',
      maxHeight: '40vw',
    });
    setTimeout(() => {
        if(this.canvas){
            const canvasEl = this.canvas.nativeElement;
            canvasEl.width = canvasEl.offsetWidth;
            canvasEl.height = canvasEl.offsetHeight;

            this.ctx = canvasEl.getContext('2d');
            this.ctx.lineWidth = 2;
            this.ctx.lineCap = 'round';
            this.ctx.strokeStyle = '#000';
        }
    }, 500);
  }

  startDrawing(e: MouseEvent | TouchEvent) {
    this.isDrawing = true;
    const { x, y } = this.getCoordinates(e);
    this.lastX = x;
    this.lastY = y;
  }

  draw(e: MouseEvent | TouchEvent) {
    if (!this.isDrawing) { return; }
    e.preventDefault();
    const { x, y } = this.getCoordinates(e);

    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();

    this.lastX = x;
    this.lastY = y;
  }

  stopDrawing() {
    this.isDrawing = false;
  }

  clearSignature() {
    if (this.ctx && this.canvas) {
        this.ctx.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
    }
  }

  private getCoordinates(e: MouseEvent | TouchEvent): { x: number, y: number } {
    const rect = this.canvas.nativeElement.getBoundingClientRect();
    let clientX, clientY;

    if (e instanceof MouseEvent) {
        clientX = e.clientX;
        clientY = e.clientY;
    } else {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
    }
    return {
        x: clientX - rect.left,
        y: clientY - rect.top
    };
  }

  fieldLabel(field: ContractField): string {
    const name = field.name || field.key || '';
    return name.length > 60 ? name.slice(0, 60) + '...' : name;
  }

  interactWithField(field: ContractField) {
      if (this.completed) { return; }
      if (this.currentSigner?.status === ContractStatusEnum.SIGNED) { return; }

      this.activeField = field;
      
      if (field.type === ContractFieldType.SIGNATURE || field.type === ContractFieldType.INITIALS) {
          this.signDocument();
      } else if (field.type === ContractFieldType.TEXT) {
      } else if (field.type === ContractFieldType.DATE) {
      }
  }

  onError(error: any) {
      console.error('PDF Viewer Error:', error);
      this.toasterService.showError('Error displaying the PDF');
  }

  zoomIn() {
    this.zoom += 0.1;
  }

  zoomOut() {
    if (this.zoom > 0.5) {
      this.zoom -= 0.1;
    }
  }

  resetZoom() {
    this.zoom = 1;
    this.fitToPage = true;
  }
  
  setFilter(type: string) {
      if(type === 'FIT_PAGE') {
          this.fitToPage = true;
          this.autoresize = true;
          this.originalSize = false;
          this.zoom = 1;
      } else if (type === 'FIT_WIDTH') {
          this.fitToPage = false;
          this.autoresize = true;
          this.originalSize = false;
          this.zoom = 1; 
      } else if (type === 'ORIGINAL') {
          this.fitToPage = false;
          this.autoresize = false;
          this.originalSize = true;
          this.zoom = 1;
      }
  }

  pdfWidth: number;
  pdfHeight: number;

  pdfLoaded: boolean = false;

  onPageRendered(e: any) {
    setTimeout(() => {
        const container = document.querySelector('.pdf-content');
        if (container) {
            const pdfPage = container.querySelector('.page') as HTMLElement;
            if (pdfPage) {
                this.pdfWidth = pdfPage.offsetWidth;
                this.pdfHeight = pdfPage.offsetHeight;
                this.pdfLoaded = true;
            }
        }
    }, 200);
  }

  async insertSignature() {
    this.loading = true;
    this.spinnerService.show();
    try {
      const canvasEl = this.canvas.nativeElement;
      const blob = await new Promise<Blob>(resolve => canvasEl.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Failed to capture signature');

      const fileName = `${this.activeField?.id || 'main'}_${this.currentSigner?.id || 'admin'}_${Date.now()}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });

      const commonPath = `${Project.PATH}/${this.projectId}/${Contract.PATH}/${this.contractId}/fields`;
      const signatureUrl = await this.storageService.saveOrRemoveImagesIfNecessary(file, commonPath);

      if (this.activeField) {
          this.activeField.value = signatureUrl;

          if (this.currentSigner) {
            await this.contractService.saveSignerFieldValues(
              this.projectId,
              this.contractId,
              this.currentSigner.id,
              this.buildFieldValues()
            );
          } else {
            await this.contractService.save(this.model, this.projectId);
          }

          this.toasterService.show('Signature added to field.', 'Success');
          this.dialogReference.close();
      } else {
          const pdfBytes = this.model.pdf
            ? await this.pdfRenderer.renderWithFields(this.model.pdf, this.model.fields).catch((): any => undefined)
            : undefined;
          const pdfBase64 = pdfBytes ? this.bytesToBase64(pdfBytes) : undefined;

          if (this.currentSigner) {
            await this.contractService.completeSignerWithMetadata({
              projectId: this.projectId,
              contractId: this.contractId,
              signerId: this.currentSigner.id,
              signatureData: signatureUrl,
              pdfBase64,
              signerEmail: this.currentSigner.email,
              signerName: this.currentSigner.name || '',
              senderEmail: this.model.senderEmail || undefined,
              workflowInstanceId: this.currentSigner.workflowInstanceId || undefined,
              workflowStepId: this.currentSigner.workflowStepId || undefined,
            });
            this.currentSigner.status = ContractStatusEnum.SIGNED;
            this.toasterService.show('Document signed correctly', 'Success');
          } else {
            this.model.status = ContractStatusEnum.SIGNED;
            this.model.signedAt = new Date();
            this.model.signedBy = this.authService.auth.user.id;
            this.model.signature = signatureUrl;
            await this.contractService.save(this.model, this.projectId);
            this.toasterService.show('Document signed correctly', 'Success');
          }
          this.dialogReference.close();
      }

    } catch (error: any) {
       console.error(error);
       this.toasterService.showError('Error saving signature.');
    } finally {
      this.loading = false;
      this.spinnerService.hide();
    }
  }

  toggleRadio(field: ContractField, value: string) {
    const targetGroup = field.groupKey;
    if (!targetGroup) {
        field.value = value;
    } else {
        this.model.fields.forEach(f => {
            if (f.groupKey === targetGroup) {
                f.value = value;
            }
        });
    }
    this.clearFieldsThatBecameHidden();
    this.saveProgress();
  }

  toggleCheckbox(field: ContractField) {
    if (field.optionValue) {
        field.value = field.value === field.optionValue ? '' : field.optionValue;
    } else {
        field.value = field.value === 'checked' ? '' : 'checked';
    }
    this.clearFieldsThatBecameHidden();
    this.saveProgress();
  }

  onCheckboxClick(field: ContractField, event: MouseEvent) {
    event.stopPropagation();
    if (this.completed) { return; }
    this.toggleCheckbox(field);
  }

  onRadioClick(field: ContractField, event: MouseEvent) {
    event.stopPropagation();
    if (this.completed) { return; }
    this.toggleRadio(field, field.optionValue || 'selected');
  }

  isOptionChecked(field: ContractField, opt: string): boolean {
    const groupKey = field.groupKey;
    if (!groupKey) { return field.value === opt; }
    const target = this.model.fields.find(f => f.groupKey === groupKey && f.optionValue === opt);
    return target ? target.value === opt : false;
  }

  toggleOption(field: ContractField, opt: string) {
    const groupKey = field.groupKey;
    if (!groupKey) {
        field.value = field.value === opt ? '' : opt;
    } else {
        const target = this.model.fields.find(f => f.groupKey === groupKey && f.optionValue === opt);
        if (target) {
            target.value = target.value === opt ? '' : opt;
        }
    }
    this.clearFieldsThatBecameHidden();
    this.saveProgress();
  }

  private captureFieldVisibility(): void {
    this.fieldVisibility.clear();
    this.model.fields?.forEach(field => {
      this.fieldVisibility.set(field.id, this.isFieldVisible(field));
    });
  }

  private clearFieldsThatBecameHidden(): void {
    this.clearNewlyHiddenValues();

    // Store the final state only after all dependent values have settled.
    this.model.fields?.forEach(field => {
      this.fieldVisibility.set(field.id, this.isFieldVisible(field));
    });
  }

  private clearNewlyHiddenValues(): void {
    const fieldsCleared = (this.model.fields ?? []).reduce((count, field) => {
      const wasVisible = this.fieldVisibility.get(field.id) ?? this.isFieldVisible(field);
      const isVisible = this.isFieldVisible(field);
      if (wasVisible && !isVisible && field.value !== '' && field.value != null) {
        field.value = '';
        return count + 1;
      }
      return count;
    }, 0);

    // A cleared parent can change another field's visibility. Each recursive
    // pass clears at least one value, so recursion is bounded by field count.
    if (fieldsCleared > 0) {
      this.clearNewlyHiddenValues();
    }
  }

  get uniqueFields(): ContractField[] {
    return this._uniqueFields;
  }

  private computeUniqueFields(): ContractField[] {
    const seenGroupKeys = new Set<string>();
    const seenKeys = new Set<string>();
    return this.model.fields.filter(f => {
      if (f.groupKey) {
          if (seenGroupKeys.has(f.groupKey)) { return false; }
          seenGroupKeys.add(f.groupKey);
          return true;
      }
      if (!f.key) { return true; }
      if (seenKeys.has(f.key)) { return false; }
      seenKeys.add(f.key);
      return true;
    });
  }

  private getEffectiveValue(f: ContractField): string {
    if (f.type === ContractFieldType.CHECKBOX && f.groupKey) {
      const groupFields = this.model.fields.filter(gf => gf.groupKey === f.groupKey);
      const checkedValues = groupFields
        .filter(gf => gf.value && gf.value === gf.optionValue)
        .map(gf => gf.optionValue);
      return checkedValues.join(',');
    }
    return f.value || '';
  }

  checkCompletion(): boolean {
      const seenCheckboxGroups = new Set<string>();
      const visibleFields = this.visibleFields;
      const invalidFields: string[] = [];

      for (const f of visibleFields) {
          if (f.type === ContractFieldType.CHECKBOX && f.groupKey) {
              if (seenCheckboxGroups.has(f.groupKey)) { continue; }
              seenCheckboxGroups.add(f.groupKey);
          }

          const val = this.getEffectiveValue(f);
          const name = this.fieldLabel(f) || 'Field';
          if (f.required && !val && f.type !== ContractFieldType.HEADERS) {
              invalidFields.push(`${name} is required`);
              continue;
          }

          if (val && f.type === ContractFieldType.EMAIL) {
              if (!/^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$/i.test(val)) {
                  invalidFields.push(`${name} must be a valid email address`);
              }
          }

          if (val && f.validation) {
              if (f.validation.minLength && val.length < f.validation.minLength) {
                  invalidFields.push(`${name} must be at least ${f.validation.minLength} characters`);
              }
              if (f.validation.maxLength && val.length > f.validation.maxLength) {
                  invalidFields.push(`${name} must be at most ${f.validation.maxLength} characters`);
              }

              if (f.validation.pattern) {
                  try {
                      const regex = new RegExp(f.validation.pattern);
                      if (!regex.test(val)) {
                          invalidFields.push(`${name} has an invalid format`);
                      }
                  } catch (e) {
                      console.error('Invalid regex pattern for field', f.key, f.validation.pattern);
                  }
              }

              if (f.type === ContractFieldType.NUMBER && val) {
                  const numVal = Number(val);
                  if (f.validation.min !== undefined && f.validation.min !== null && numVal < f.validation.min) {
                      invalidFields.push(`${name} must be at least ${f.validation.min}`);
                  }
                  if (f.validation.max !== undefined && f.validation.max !== null && numVal > f.validation.max) {
                      invalidFields.push(`${name} must be at most ${f.validation.max}`);
                  }
              }
          }
      }
      
      if (invalidFields.length > 0) {
          this.toasterService.showError('Please fix the following issues: ' + invalidFields.join(', '));
          return false;
      }

      if (!this.currentSigner) {
        this.model.status = ContractStatusEnum.SIGNED;
        this.model.signedAt = new Date();
        this.model.signedBy = this.authService.auth.user.id;
      }
      return true;
  }

  canComplete(): boolean {
      const seenCheckboxGroups = new Set<string>();
      const visibleFields = this.visibleFields;
      for (const f of visibleFields) {
          if (f.type === ContractFieldType.CHECKBOX && f.groupKey) {
              if (seenCheckboxGroups.has(f.groupKey)) { continue; }
              seenCheckboxGroups.add(f.groupKey);
          }
          const val = this.getEffectiveValue(f);
          if (f.required && !val && f.type !== ContractFieldType.HEADERS) { return false; }

          if (val && f.type === ContractFieldType.EMAIL) {
              if (!/^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$/i.test(val)) { return false; }
          }

          if (val && f.validation) {
              if (f.validation.minLength && val.length < f.validation.minLength) { return false; }
              if (f.validation.maxLength && val.length > f.validation.maxLength) { return false; }
              if (f.validation.pattern) {
                  try {
                      if (!new RegExp(f.validation.pattern).test(val)) { return false; }
                  } catch (e) {}
              }
              if (f.type === ContractFieldType.NUMBER) {
                  const numVal = Number(val);
                  if (f.validation.min !== undefined && f.validation.min !== null && numVal < f.validation.min) { return false; }
                  if (f.validation.max !== undefined && f.validation.max !== null && numVal > f.validation.max) { return false; }
              }
          }
      }
      return true;
  }
  
  private buildFieldValues(): { [fieldId: string]: string } {
    this.clearFieldsThatBecameHidden();
    const map: { [fieldId: string]: string } = {};
    this.model.fields?.forEach(f => {
      if (f.type === ContractFieldType.FILE) { return; }
      if (!this.currentSigner?.role || !f.signerRole || f.signerRole === this.currentSigner.role) {
        map[f.id] = f.value || '';
      }
    });
    return map;
  }

  async saveProgress() {
    this.clearFieldsThatBecameHidden();
    if (this.currentSigner) {
      await this.contractService.saveSignerFieldValues(
        this.projectId,
        this.contractId,
        this.currentSigner.id,
        this.buildFieldValues()
      );
    } else {
      await this.contractService.save(this.model, this.projectId);
    }
    this.refreshFormGroups();
  }

  onDateClick(input: HTMLInputElement, event: MouseEvent) {
    event.stopPropagation();
    if (!this.completed) { (input as any).showPicker?.(); }
  }

  onNumberInput(field: ContractField, event: Event) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
    field.value = input.value;
  }

  onNumberBlur(field: ContractField) {
    if (field.value === '' || field.value == null) {
      field.value = '0';
    }
    this.saveProgress();
  }

  get isLastStep(): boolean {
    return this.currentStepIndex >= this.pagesWithFormGroups.length - 1;
  }

  validateCurrentStep(): boolean {
    if (!this.pagesWithFormGroups || this.pagesWithFormGroups.length === 0) return true;
    const currentGroup = this.pagesWithFormGroups[this.currentStepIndex];
    if (!currentGroup) return true;

    const fieldsToValidate: ContractField[] = [
      ...currentGroup.independentFields,
      ...(currentGroup.tables ? currentGroup.tables.flatMap((t: any) => t.columns.flatMap((c: any) => c.fields)) : [])
    ];

    const seenCheckboxGroups = new Set<string>();
    const errors: string[] = [];

    for (const f of fieldsToValidate) {
      if (!this.isFieldVisible(f)) continue;
      if (f.type === ContractFieldType.CHECKBOX && f.groupKey) {
        if (seenCheckboxGroups.has(f.groupKey)) continue;
        seenCheckboxGroups.add(f.groupKey);
      }
      const val = this.getEffectiveValue(f);
      const name = this.fieldLabel(f) || 'Field';

      if (f.required && !val && f.type !== ContractFieldType.HEADERS) {
        errors.push(`${name} is required`);
        continue;
      }
      if (val && f.type === ContractFieldType.EMAIL) {
        if (!/^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$/i.test(val)) {
          errors.push(`${name} must be a valid email address`);
        }
      }
      if (val && f.validation) {
        if (f.validation.minLength && val.length < f.validation.minLength) {
          errors.push(`${name} must be at least ${f.validation.minLength} characters`);
        }
        if (f.validation.maxLength && val.length > f.validation.maxLength) {
          errors.push(`${name} must be at most ${f.validation.maxLength} characters`);
        }
        if (f.validation.pattern) {
          try {
            if (!new RegExp(f.validation.pattern).test(val)) errors.push(`${name} has an invalid format`);
          } catch (e) {}
        }
      }
    }

    if (errors.length > 0) {
      this.toasterService.showError(`Please fix the following: ${errors.join(', ')}`);
      return false;
    }
    return true;
  }

  nextStep() {
    if (this.validateCurrentStep()) {
      if (!this.isLastStep) {
        this.currentStepIndex++;
        this.saveProgress();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        this.finishForm();
      }
    }
  }

  prevStep() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
      this.saveProgress();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  get isEmployee(): boolean {
    return !!this.authService.auth?.employee;
  }

  async saveAndExit() {
    this.loading = true;
    try {
      if (this.currentSigner) {
        await this.contractService.saveSignerFieldValues(
          this.projectId,
          this.contractId,
          this.currentSigner.id,
          this.buildFieldValues()
        );
        await this.contractService.setSignerStatus(
          this.projectId,
          this.contractId,
          this.currentSigner.id,
          ContractStatusEnum.PARTIALLY_SIGNED
        );
        this.currentSigner.status = ContractStatusEnum.PARTIALLY_SIGNED;
      } else {
        this.model.status = ContractStatusEnum.PARTIALLY_SIGNED;
        await this.contractService.save(this.model, this.projectId);
      }
      this.toasterService.show('Progress saved correctly.', 'Success');
    } catch (e) {
      this.toasterService.showError('Error saving progress.');
    } finally {
      this.loading = false;
    }
  }

  async finishForm() {
    const seenCheckboxGroups = new Set<string>();
    const visibleFields = this.visibleFields;
    const errors: string[] = [];

    for (const f of visibleFields) {
      if (f.type === ContractFieldType.CHECKBOX && f.groupKey) {
        if (seenCheckboxGroups.has(f.groupKey)) continue;
        seenCheckboxGroups.add(f.groupKey);
      }
      const val = this.getEffectiveValue(f);
      const name = this.fieldLabel(f) || 'Field';

      if (f.required && !val && f.type !== ContractFieldType.HEADERS) {
        errors.push(`${name} is required`);
        continue;
      }
      if (val && f.type === ContractFieldType.EMAIL) {
        if (!/^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$/i.test(val)) {
          errors.push(`${name} must be a valid email address`);
        }
      }
      if (val && f.validation) {
        if (f.validation.minLength && val.length < f.validation.minLength) {
          errors.push(`${name} must be at least ${f.validation.minLength} characters`);
        }
        if (f.validation.maxLength && val.length > f.validation.maxLength) {
          errors.push(`${name} must be at most ${f.validation.maxLength} characters`);
        }
        if (f.validation.pattern) {
          try {
            if (!new RegExp(f.validation.pattern).test(val)) errors.push(`${name} has an invalid format`);
          } catch (e) {}
        }
      }
    }

    if (errors.length > 0) {
      this.toasterService.showError(`Please fix the following: ${errors.join(', ')}`);
      return;
    }

    this.loading = true;
    this.spinnerService.show();
    try {
      const pdfBytes = this.model.pdf
        ? await this.pdfRenderer.renderWithFields(this.model.pdf, this.model.fields).catch((): any => undefined)
        : undefined;
      const pdfBase64 = pdfBytes ? this.bytesToBase64(pdfBytes) : undefined;

      if (this.currentSigner) {
        const fileAttachmentUrls = (this.model.fields || [])
          .filter(f => f.type === ContractFieldType.FILE && f.value && (!f.signerRole || f.signerRole === this.currentSigner.role))
          .map(f => f.value as string);
        await this.contractService.completeSignerWithMetadata({
          projectId: this.projectId,
          contractId: this.contractId,
          signerId: this.currentSigner.id,
          fieldValues: this.buildFieldValues(),
          pdfBase64,
          signerEmail: this.currentSigner.email,
          signerName: this.currentSigner.name || '',
          senderEmail: this.model.senderEmail || undefined,
          fileAttachmentUrls: fileAttachmentUrls.length ? fileAttachmentUrls : undefined,
          workflowInstanceId: this.currentSigner.workflowInstanceId || undefined,
          workflowStepId: this.currentSigner.workflowStepId || undefined,
        });
        this.currentSigner.status = ContractStatusEnum.SIGNED;
      } else {
        this.model.status = ContractStatusEnum.SIGNED;
        await this.contractService.save(this.model, this.projectId);
      }
      this.completed = true;
      this.viewMode = 'PDF';
      this.toasterService.show('Form completed correctly.', 'Success');
    } catch (e) {
      this.toasterService.showError('Error saving the form.');
    } finally {
      this.loading = false;
      this.spinnerService.hide();
    }
  }

  isFullWidth(field: ContractField): boolean {
    const fullWidthTypes = [
      ContractFieldType.TEXTAREA,
      ContractFieldType.HEADERS,
      ContractFieldType.IMAGE,
      ContractFieldType.SIGNATURE,
      ContractFieldType.INITIALS,
      ContractFieldType.FILE,
    ];
    if (fullWidthTypes.includes(field.type)) return true;
    return false;
  }

  isFieldVisible(field: ContractField): boolean {
    if (!field.conditionalLogic || !field.conditionalLogic.dependsOnFieldId) {
      return true;
    }

    const parentField = this.model.fields.find(f => f.id === field.conditionalLogic.dependsOnFieldId);
    if (!parentField) { return true; }

    let effectiveValue: string;

    if (parentField.groupKey) {
      const groupFields = this.model.fields.filter(f => f.groupKey === parentField.groupKey);

      if (parentField.type === ContractFieldType.RADIO) {
        effectiveValue = groupFields.find(f => !!f.value)?.value ?? '';
      } else if (parentField.type === ContractFieldType.CHECKBOX) {
        effectiveValue = groupFields
          .filter(f => f.value && f.value === f.optionValue)
          .map(f => f.optionValue)
          .join(',');
      } else {
        effectiveValue = parentField.value ?? '';
      }
    } else {
      effectiveValue = parentField.value ?? '';
    }

    const val    = effectiveValue.toString().toLowerCase();
    const target = (field.conditionalLogic.value ?? '').toString().toLowerCase();

    switch (field.conditionalLogic.operator) {
      case 'equals':     return val === target;
      case 'notEquals':  return val !== target;
      case 'includes':   return val.includes(target);
      case 'startsWith': return val.startsWith(target);
      case 'endsWith':   return val.endsWith(target);
      default:           return true;
    }
  }

  get visibleFields(): ContractField[] {
    return this.model.fields.filter(f => this.isFieldVisible(f) && f.type !== ContractFieldType.IMAGE);
  }
  
  refreshFormGroups() {
    this._uniqueFields = this.computeUniqueFields();
    const unique = this._uniqueFields;
    const tableFieldIds: string[] = [];
    
    if (this.model.tables) {
      this.model.tables.forEach(table => {
        table.columns.forEach(col => {
          tableFieldIds.push(...col.fieldIds);
        });
      });
    }

    let tablesWithFieldsArray: any[] = [];
    if (this.model.tables) {
       tablesWithFieldsArray = this.model.tables.map(table => {
        return {
          ...table,
          columns: table.columns.map(col => ({
            ...col,
            fields: col.fieldIds
              .map(id => this.model.fields.find(f => f.id === id))
              .filter(f => f && this.isFieldVisible(f) && f.type !== ContractFieldType.IMAGE)
          }))
        };
      }).filter(table => table.columns.some(c => c.fields.length > 0));
    }
    this._tablesWithFields = tablesWithFieldsArray;

    const independentFields = unique.filter(f => 
      this.isFieldVisible(f) && 
      !tableFieldIds.includes(f.id) &&
      f.type !== ContractFieldType.IMAGE
    );
    this._independentFields = independentFields;

    const pagesMap = new Map<number, { page: number, independentFields: ContractField[], tables: any[] }>();
    const getPageEntry = (page: number) => {
      if (!pagesMap.has(page)) {
        pagesMap.set(page, { page, independentFields: [], tables: [] });
      }
      return pagesMap.get(page)!;
    };

    independentFields.forEach(f => {
      const page = Math.floor((f.y || 0) / 100) + 1;
      getPageEntry(page).independentFields.push(f);
    });

    tablesWithFieldsArray.forEach((table: any) => {
      let firstField: ContractField | undefined;
      for (const col of table.columns) {
        if (col.fields && col.fields.length > 0) {
          firstField = col.fields[0];
          break;
        }
      }
      if (firstField) {
        const page = Math.floor((firstField.y || 0) / 100) + 1;
        getPageEntry(page).tables.push(table);
      }
    });

    this._pagesWithFormGroups = Array.from(pagesMap.values()).sort((a, b) => a.page - b.page);
  }

  get independentFields(): ContractField[] {
    return this._independentFields;
  }
  
  get tablesWithFields(): any[] {
    return this._tablesWithFields;
  }

  get pagesWithFormGroups(): any[] {
    return this._pagesWithFormGroups;
  }

  getProgress(): number {
    if (!this.pagesWithFormGroups || this.pagesWithFormGroups.length <= 1) return 100;
    return (this.currentStepIndex / (this.pagesWithFormGroups.length - 1)) * 100;
  }

  switchView(mode: 'FORM' | 'PDF') {
    this.viewMode = mode;
  }

  backToForm() {
    this.completed = false;
    this.viewMode = 'FORM';
    this.currentStepIndex = 0;
    if (this.currentSigner) {
      this.currentSigner.status = ContractStatusEnum.PARTIALLY_SIGNED;
      this.contractService.setSignerStatus(
        this.projectId,
        this.contractId,
        this.currentSigner.id,
        ContractStatusEnum.PARTIALLY_SIGNED
      ).catch(e => console.error('Error resetting signer status:', e));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  downloadingPdf = false;



  async onFieldFileSelected(event: Event, field: ContractField) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) { return; }
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      this.toasterService.showError('Only images or PDF files are allowed.');
      input.value = '';
      return;
    }
    this.loading = true;
    this.spinnerService.show();
    try {
      const basePath = `${Project.PATH}/${this.projectId}/${Contract.PATH}/${this.contractId}/fields`;
      const namedFile = new File([file], `${field.id}_${Date.now()}_${file.name}`, { type: file.type });
      const url = await this.storageService.uploadFile(basePath + '/' + namedFile.name, namedFile, { contentType: file.type });
      field.value = url;
      await this.saveProgress();
    } catch (e) {
      this.toasterService.showError('Error uploading file.');
    } finally {
      this.loading = false;
      this.spinnerService.hide();
      input.value = '';
    }
  }

  private bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) { binary += String.fromCharCode(bytes[i]); }
    return btoa(binary);
  }

  async downloadPdf() {
    if (!this.model.pdf) { return; }
    this.downloadingPdf = true;
    try {
      const bytes = await this.pdfRenderer.renderWithFields(this.model.pdf, this.model.fields);
      const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.model.name || 'contrato'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error generating PDF', e);
      this.toasterService.showError('Error generating PDF.');
    } finally {
      this.downloadingPdf = false;
    }
  }

  canSign(): boolean {
    return !!this.projectId && !!this.contractId && !!this.model && !this.loadError && !this.isLoading && !this.completed;
  }

}
